import * as React from "react";
import { Sidebar } from "@/components/ui/sidebar";
import { useTheme } from "@/contexts/theme-context";
import { cn } from "@/lib/utils";

type SidebarProps = React.ComponentProps<typeof Sidebar>;

interface AdvaSidebarProps extends Omit<
  SidebarProps,
  "data-theme" | "variant" | "collapsible"
> {
  /**
   * `"collapsible"` (default) — shadcn `collapsible="offcanvas"` defaults;
   * used by the dashboard app layout.
   * `"static"` — shadcn `collapsible="none"` plus `h-svh sticky top-0
   * overflow-y-auto`; used by nested layouts (settings) where the sidebar
   * is always open and scrolls with the page.
   */
  variant?: "collapsible" | "static";
}

export const AdvaSidebar = React.forwardRef<HTMLDivElement, AdvaSidebarProps>(
  ({ variant = "collapsible", className, children, ...rest }, ref) => {
    const { sidebarTheme } = useTheme();
    const isStatic = variant === "static";

    return (
      <Sidebar
        ref={ref}
        data-theme={sidebarTheme}
        collapsible={isStatic ? "none" : undefined}
        className={cn(
          isStatic && "h-svh sticky top-0 overflow-y-auto",
          className,
        )}
        {...rest}
      >
        {children}
      </Sidebar>
    );
  },
);
AdvaSidebar.displayName = "AdvaSidebar";
